/**
 * FormContextMenu Component
 *
 * Provides a right-click context menu for the form page with workflow actions.
 * Actions mirror FormWorkflowSelect: Share, Set State, Create Form, Version, Archive.
 *
 * The workflow, state-change, export, email, and close-&-create actions are owned
 * by FormWorkflowActionProvider (shared with the toolbar dropdown and the command
 * palette), so this component only renders the menu and owns the local archive
 * and create-version actions. The "Create new form" entries call
 * `onCreateNextForm`, which closes an open source version through the shared
 * Close & Create drawer before the next form exists (SYD-2816).
 *
 * Reads the store through a narrow snapshot — ids, form number, form-type labels
 * — never `useFormStore()`, whose spread of `formVersion.data` would re-render
 * this menu on every keystroke in the form.
 *
 * It is also the form's ONLY field menu (SYD-2232): a capture-phase right-click
 * records the `data-form-field-key` of the field under the pointer — `null` when
 * there is none, which is what keeps Copy Field and Paste Field from acting on the
 * last field touched — and `formFieldClipboard` resolves it to that field's own
 * reader and writer. Copy and paste share the ArTable's wire format, so a table
 * cell pastes into a form field and back.
 */

import { FC, ReactNode, MouseEvent, useCallback, useContext, useState, useId } from 'react';
import { Modal, Tooltip } from 'antd';
import {
  MailOutlined,
  FilePdfOutlined,
  FileAddOutlined,
  EditOutlined,
  HistoryOutlined,
  DeleteOutlined,
  CopyOutlined,
  SnippetsOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { formActions, formStore } from '../../../store';
import { formFieldClipboard } from '../../../store/form';
import { MessageContext } from '../../../providers/MessageContext';
import { ContextMenu } from '../../../providers/ContextMenu';
import {
  buildEnvelope,
  coerceToColumnValue,
  computePasteValues,
  parseClipboard,
  readSystemClipboard,
  serializeArCells,
  writeSystemClipboard,
} from '../../ArTable/clipboard';
import type { ColumnClipboardTarget } from '../../ArTable/clipboard';
import {
  FormPropsUIDescription,
  formStateDescription,
  MULTILINE_TEXT_FIELD_DATA_TYPES,
  type FormStateId,
} from '#types';
import { createFormVersion } from '../../../services/artifactFunctions';
import { useFormWorkflowAction } from '../../../providers/artifacts/FormWorkflowActionContext';
import { isCloseAndCreateTargetState } from '../../../use/form/isCloseAndCreateTargetState';
import { isDraftFormState } from '../../../use/form/isDraftFormState';
import { draftFormStateId } from '../../../use/form/draftFormStateId';
import { useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { checkRequiredFields } from '../../../use/form/checkRequiredFields';

type FormContextMenuProps = {
  formId: string;
  children: ReactNode;
};

/** The DOM attribute every field surface stamps with its `formFieldClipboard` key. */
const fieldKeyAttribute = 'data-form-field-key';

/**
 * FormContextMenu wraps form content and provides right-click context menu.
 */
const FormContextMenu: FC<FormContextMenuProps> = ({
  formId,
  children,
}) => {
  const menuId = useId();
  const navigate = useNavigate();
  const { messageApi } = useContext(MessageContext);
  const snap = useSnapshot(formStore.state);
  const versionId = snap.versionId;
  const formNumber = snap.form.data?.formNumber;
  const currentVersionId = snap.form.data?.currentVersionId;
  const formTypeDescription = (formTypeId: string | undefined) =>
    formTypeId ? snap.formTypes.docs.get(formTypeId)?.data?.description : undefined;

  const {
    isCreating,
    setIsCreating,
    allowedNextStates,
    allowedNextForms,
    onOpenCloseAndCreate,
    isCloseAndCreateEntryDisabled,
    onCreateNextForm,
    isCreateNextFormDisabled,
    applicableWorkflows,
    handleWorkflowClick,
    handleSetState,
    handleExport,
    handleEmailClick,
  } = useFormWorkflowAction();
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  /**
   * The field the last right-click landed on, or `null` for a click on form chrome.
   * Render state, because the two field items' `disabled` flags are read from it;
   * the children's identity is stable, so the subtree bails out of the re-render.
   */
  const [fieldKey, setFieldKey] = useState<string | null>(null);

  /**
   * Capture phase, so it runs before the menu opens and before the field's own
   * `ContextMenuWrapper` stops the event bubbling. Recording the miss is the point:
   * a stale key would leave both items enabled over form chrome.
   */
  const handleContextMenuCapture = useCallback((e: MouseEvent<HTMLDivElement>) => {
    const target = e.target;
    setFieldKey(
      target instanceof HTMLElement
        ? target.closest(`[${fieldKeyAttribute}]`)?.getAttribute(fieldKeyAttribute) ?? null
        : null,
    );
  }, []);

  const fieldTarget = fieldKey === null ? undefined : formFieldClipboard.targetFor(fieldKey);

  const handleCopyField = async (): Promise<void> => {
    if (fieldKey === null) return;
    const target = formFieldClipboard.targetFor(fieldKey);
    if (!target) {
      messageApi.info('That field is no longer on screen.');
      return;
    }
    const column: ColumnClipboardTarget = {
      kind: 'field',
      fieldType: target.fieldType,
      pasteable: target.write !== null,
    };
    const payload = serializeArCells(
      [buildEnvelope(target.fieldId, target.read() ?? null, column)],
      target.fieldType,
    );
    const written = await writeSystemClipboard(payload);
    messageApi.info(written ? 'Copied' : 'Clipboard access was blocked.');
  };

  /**
   * The target is resolved AFTER the clipboard read, never captured across it: a
   * field's own gate can close while the read is in flight — a recovery preview
   * opening is exactly that — and the stale writer would refuse or, worse, not.
   *
   * External text into a TextArea-rendered field keeps its newlines: without that
   * branch `parseClipboard` splits on them and a pasted paragraph would lose
   * everything after the first line. Emptiness is tested before the branch, never
   * after: the branch always yields one value, so an empty or image-only clipboard
   * would otherwise arrive as `null` and clear a multi-line field.
   */
  const handlePasteField = async (): Promise<void> => {
    if (fieldKey === null) return;
    const payload = await readSystemClipboard();
    if (payload === null) {
      messageApi.info('Clipboard access was blocked.');
      return;
    }
    const target = formFieldClipboard.targetFor(fieldKey);
    if (!target?.write) {
      messageApi.info('That field no longer accepts changes.');
      return;
    }
    const parsed = parseClipboard(payload);
    if (parsed.kind === 'external' && parsed.lines.length === 0) {
      messageApi.info('Nothing to paste.');
      return;
    }
    const column: ColumnClipboardTarget = {
      kind: 'field',
      fieldType: target.fieldType,
      pasteable: true,
    };
    const values =
      parsed.kind === 'external' &&
      MULTILINE_TEXT_FIELD_DATA_TYPES.has(target.fieldType?.fieldDataTypeId ?? '')
        ? [coerceToColumnValue({ kind: 'external', raw: payload.getData('text/plain') }, column)]
        : computePasteValues(parsed, column);
    if (values.length === 0) {
      messageApi.info('Nothing to paste.');
      return;
    }
    const [next, ...rest] = values;
    if (next === undefined) {
      messageApi.info('The clipboard value does not fit this field.');
      return;
    }
    target.write(next);
    if (rest.length > 0) messageApi.info(`Pasted the first of ${values.length} values.`);
  };

  const handleCreateVersion = async (state: FormStateId) => {
    if (!versionId) return;
    if (!checkRequiredFields()) return;

    setIsCreating(true);
    try {
      const newVersionId = await createFormVersion(formId, versionId, state);
      formActions.navigateToVersion(formId, newVersionId);
    } catch (err) {
      messageApi.error(`Error creating new version: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleArchiveClick = () => {
    setShowArchiveModal(true);
  };

  const handleArchiveConfirm = async () => {
    setShowArchiveModal(false);

    const archivedFormNumber = formNumber || '-';

    navigate('/forms');

    setTimeout(async () => {
      const archived = await formActions.archiveForm(formId);

      if (archived) {
        messageApi.success(`Form ${archivedFormNumber} archived successfully`);
      } else {
        messageApi.error(`Failed to archive form ${archivedFormNumber}`);
      }
    }, 100);
  };

  const items: MenuProps['items'] = [
    {
      key: 'copy-field',
      label: 'Copy Field',
      icon: <CopyOutlined />,
      onClick: () => void handleCopyField(),
      disabled: fieldTarget === undefined,
    },
    {
      key: 'paste-field',
      label: 'Paste Field',
      icon: <SnippetsOutlined />,
      onClick: () => void handlePasteField(),
      disabled: fieldTarget?.write == null,
    },
    { type: 'divider' as const },
    {
      key: 'share',
      type: 'group' as const,
      label: 'Share',
      children: [
        {
          key: 'email',
          label: 'Send via Email',
          icon: <MailOutlined />,
          onClick: handleEmailClick,
          disabled: isCreating,
        },
        {
          key: 'pdf',
          label: 'Export as PDF',
          icon: <FilePdfOutlined />,
          onClick: handleExport,
          disabled: isCreating,
        },
      ]
    },
    { type: 'divider' as const },
    ...(applicableWorkflows.length > 0
      ? ([
        {
          key: 'workflows',
          type: 'group' as const,
          label: 'Workflows',
          children: applicableWorkflows.map((w) => ({
            key: `workflow-${w.docId}`,
            icon: <FileAddOutlined />,
            label: (
              <Tooltip title={w.description?.longDescription}>
                {w.description?.shortLabel || w.docId}
              </Tooltip>
            ),
            onClick: () => {
              void handleWorkflowClick(w);
            },
            disabled: isCreating,
          })),
        },
        { type: 'divider' as const },
      ] as NonNullable<MenuProps['items']>)
      : []),
    {
      key: 'close-create',
      label: 'Close & Create',
      icon: <FileAddOutlined />,
      onClick: onOpenCloseAndCreate,
      disabled: isCloseAndCreateEntryDisabled,
    },
    { type: 'divider' as const },
    {
      key: 'state',
      type: 'group' as const,
      label: 'Set form to',
      children: (allowedNextStates?.length ?? 0) > 0
        ? allowedNextStates
          ?.filter(isCloseAndCreateTargetState)
          .map((state: FormStateId) => ({
            key: `state-${state}`,
            icon: <EditOutlined />,
            label: (
              <Tooltip title={formStateDescription[state]?.longDescription}>
                {formStateDescription[state]?.shortLabel || state}
              </Tooltip>
            ),
            onClick: () => handleSetState(state),
            disabled: isCreating,
          })) ?? []
        : [{
            key: 'no-states',
            label: 'Not allowed',
            disabled: true
          }]
    },
    { type: 'divider' as const },
    {
      key: 'form',
      type: 'group' as const,
      label: 'Create new form',
      children: (allowedNextForms?.length ?? 0) > 0
        ? allowedNextForms?.map(formType => ({
          key: `form-${formType}`,
          icon: <FileAddOutlined />,
          label: (
            <Tooltip title={formTypeDescription(formType)?.longDescription}>
              {formTypeDescription(formType)?.shortLabel || formType}
            </Tooltip>
          ),
          onClick: () => onCreateNextForm(formType),
          disabled: isCreateNextFormDisabled,
        })) ?? []
        : [{
            key: 'no-forms',
            label: 'Not allowed',
            disabled: true
          }]
    },
    { type: 'divider' as const },
    {
      key: 'version',
      type: 'group' as const,
      label: FormPropsUIDescription.formVersionNumber.shortLabel,
      children: [
        ...(allowedNextStates?.some(isDraftFormState)
          ? [{
            key: 'state-draft',
            icon: <HistoryOutlined />,
            label: (
              <Tooltip title={formStateDescription[draftFormStateId]?.longDescription}>
                New {formStateDescription[draftFormStateId]?.shortLabel || 'draft'} {FormPropsUIDescription.formVersionNumber.shortLabel}
              </Tooltip>
            ),
            onClick: () => handleCreateVersion(draftFormStateId),
            disabled: isCreating,
          }]
          : [{
              key: 'no-draft',
              label: 'Not allowed',
              disabled: true
            }]),
        {
          key: 'goto-current-version',
          icon: <HistoryOutlined />,
          label: 'Go to Current Version',
          onClick: () => {
            if (currentVersionId) {
              formActions.navigateToVersion(formId, currentVersionId);
            }
          },
          disabled: !currentVersionId || versionId === currentVersionId || isCreating,
        }
      ]
    },
    { type: 'divider' as const },
    {
      key: 'archive',
      label: 'Archive Form',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleArchiveClick,
      disabled: isCreating,
    }
  ];

  return (
    <>
      <ContextMenu
        menuId={menuId}
        items={items}
        disabled={isCreating}
        onContextMenuCapture={handleContextMenuCapture}
      >
        {children}
      </ContextMenu>

      <Modal
        title={
          <span>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
            Archive form?
          </span>
        }
        open={showArchiveModal}
        onCancel={() => setShowArchiveModal(false)}
        onOk={handleArchiveConfirm}
        okText="Archive"
        okType="danger"
        cancelText="Cancel"
        confirmLoading={snap.isArchivingForm}
        centered
      >
        Form {formNumber || 'this form'} and all its versions will be moved to archive. You can restore it later from the Archive section.
      </Modal>
    </>
  );
};

export default FormContextMenu;
